import React, { useCallback, useEffect, useRef, useState } from 'react';

import { datePadStart } from '../../../../helpers';
import { useTranslation } from '../../../../hooks';
import { T } from '../../../../constants';

interface TournamentInfoModalCountdownProps {
  startDate: string;
  endDate: string;
  onFinish: () => void;
}

const initialTimer = { days: 0, hours: 0, minutes: 0, seconds: 0 };

export const TournamentInfoModalCountdown: React.FC<TournamentInfoModalCountdownProps> = ({
  startDate,
  endDate,
  onFinish
}) => {

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [timer, setTimer] = useState(initialTimer);
  const [title, setTitle] = useState('');

  const t = useTranslation();

  const clearTimerInterval = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
  }, []);

  const updateTimer = useCallback(() => {
    // Get todays date and time
    const now = Date.now();
    // start and end dates by ms
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    const title = start <= now ? T.ENDS_IN : T.STARTS_IN;
    setTitle(title);

    /**
     * @description The tournament can already be over by the time the popup mounts - the promo stays
     * visible while its web active date outlives the end date, and the game load between a refresh and
     * `gameReady` can span the last seconds. Finish here too, otherwise the popup stays open on 00:00:00:00.
     */
    if (end <= now) {
      setTimer({ ...initialTimer });
      clearTimerInterval();
      onFinish();
      return;
    }

    // target date by ms
    const target = start <= now ? end : start;
    // find the duration between now an the target date
    const duration = target - now;
    // Time calculations for days, hours, minutes and seconds
    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((duration / 1000 / 60) % 60);
    const seconds = Math.floor((duration / 1000) % 60);

    // Output the result in elements
    setTimer({ days, hours, minutes, seconds });

    if (duration / 1000 < 1) {
      clearTimerInterval();
      onFinish();
    }

  }, [startDate, endDate, onFinish, clearTimerInterval]);

  useEffect(() => {
    // Run once up front so the popup never shows the initial zeros for the first interval tick
    updateTimer();
    intervalIdRef.current = setInterval(updateTimer, 900);
    return () => {
      clearTimerInterval();
    };
  }, [updateTimer, clearTimerInterval]);

  return (
    <div className="tournament-countdown-holder tournament-popup-countdown-holder">
      <div className=" tournament-popup-countdown-title-block">
        <p className="tournament-popup-countdown-title">{t(title)}</p>
      </div>
      <div className="tournament-countdown">
        <div className="tournament-countdown-item">
          <p className="tournament-countdown-time">{datePadStart(timer.days)}</p>
          <p className="tournament-countdown-txt">{t(T.DAYS_TOURNAMENT)}</p>
        </div>
        <div className="tournament-countdown-divider">:</div>
        <div className="tournament-countdown-item">
          <p className="tournament-countdown-time">{datePadStart(timer.hours)}</p>
          <p className="tournament-countdown-txt">{t(T.HRS_TOURNAMENT)}</p>
        </div>
        <div className="tournament-countdown-divider">:</div>
        <div className="tournament-countdown-item">
          <p className="tournament-countdown-time">{datePadStart(timer.minutes)}</p>
          <p className="tournament-countdown-txt">{t(T.MINS_TOURNAMENT)}</p>
        </div>
        <div className="tournament-countdown-divider">:</div>
        <div className="tournament-countdown-item">
          <p className="tournament-countdown-time">{datePadStart(timer.seconds)}</p>
          <p className="tournament-countdown-txt">{t(T.SECS_TOURNAMENT)}</p>
        </div>
      </div>
    </div>
  );
};
